import React, {useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesLeft, faAnglesRight, faArrowLeftLong, faBug, faChevronDown, faChevronLeft, faChevronRight, faCirclePlus, faPlus, faUpload, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ConfigurationComponent, InputProps } from '../../components/app/ocr/ConfigurationComponent';
import { ApiPDF } from '../../apis/ApiPDF';
import { ConfigurationJSONProps,FileWithTextBlockAndPageDataProps, TextBlocksFromPagesInputProps } from '../../types/orc/PDFTypes';
import { ConfigurationModal } from '../../components/app/ocr/ConfigurationModal';
import { base64ToFile, downloadJSON } from '../../utils/fileHandling';
import { stateIconDone, stateIconError, stateIconWaiting } from '../../types/ai/states';
import { useNavigateBack } from '../../hooks/useNavigationBack';

const defaultSampleOutputAreaSize = "18%";

export default function DocumentToPDF() {
  const { t } = useTranslation('toolsApps');
  const navigateBack = useNavigateBack();
  const [showTextBlocks, setShowTextBlocks] = useState(false);
  const [state,setState]= useState<null|string>(null);
  const [configurationName, setConfigurationName] = useState('');
  const [configurationsJSON, setConfigurationsJSON] = useState('');
  const [configurationInputs, setConfigurationInputs] = useState<InputProps[]>([]);
  const [ocrConfiguration, setOcrConfiguration] = useState('--psm 6');

  //information for selected file
  const [currentTextBlocks, setCurrentTextBlocks] = useState<TextBlocksFromPagesInputProps[]>([]);
  const [selectedPage, setSelectedPage] = useState<number|null>(null);
  //all files
  const [extractedInformationsFromAllFiles, setExtractedInformationsFromAllFiles] = useState<FileWithTextBlockAndPageDataProps[]>([]);

  const [backgroundImage, setBackgroundImage] = useState("");
  const emptyInput = {
    fieldName: '',
    dropdownSelectAI: 'With AI',
    requestForAI: ''
  };

  const [sampleOutputAreaSize, setSampleOutputAreaSize] = useState(defaultSampleOutputAreaSize);
  const [showOutoutContent, setShowOutputContent] = useState<'sampleOutput' | 'fullText' | 'debug'>('sampleOutput');
  const [configurationUploadError, setConfigurationUploadError] = useState('');

  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [scaleParam, setScaleParam] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [textBlockHTMElement, setTextBlockHTMLElement] = useState<JSX.Element[]>([]);
  const [selectedTextBlock, setSelectedTextBlock] = useState<number|null>(null);

  //Functions
  const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0]  : null;
    setState('send');
    if(file){
      let dataURL = URL.createObjectURL(file);
      //if file is png only one file in list
      let images = [file]
      //convert pdfUrl to image url if file is pdf
      if(file.name.split('.')[file.name.split('.').length-1] === 'pdf'){
        let formDataForPDF = new FormData();
        formDataForPDF.append('file',file);
        setState('waiting');
        try{
          //for each page in pdf one image
          let imagesBase64 = await ApiPDF.ConvertPdfToPng(formDataForPDF);
          images = imagesBase64.map( (x,index) => base64ToFile(x, file.name + 'page_'+String(index)));
        }
        catch{
          setState('error');
          return;
        }
      }

      let newCurrentTextBlockInformation: TextBlocksFromPagesInputProps[] = [];
      let pageNumber = 0;
      //extract textblock from all images    
      for(let img of images){     
        let newAttachedFile: FormData = new FormData();
        newAttachedFile.append('file', img);
        try{
          //Extract Text from Images
          let response = await ApiPDF.ExtractTextWithCoordinates(newAttachedFile,ocrConfiguration);
          pageNumber += 1;
          let imgURL = URL.createObjectURL(img);
          //if now textblock from image
          if(response.length < 1){
            newCurrentTextBlockInformation.push({
              imageWidth: 0,
              imageHeight: 0,
              pageNumber: pageNumber,
              image: imgURL,
              text: "",
              textblocks:[]
            });
            continue;
          }
          newCurrentTextBlockInformation.push({
            imageWidth: response[0].coordinates.image_width,
            imageHeight: response[0].coordinates.image_height,
            pageNumber: pageNumber,
            image: imgURL,
            text: response.map(x => x.text).join(" "),
            textblocks: response.map(x => {
              return{
                texts: x.text,
                left: x.coordinates.x,
                top: x.coordinates.y,
                width: x.coordinates.width,
                height: x.coordinates.height,
                // par_num: x.coordinates.par_num,
                // block_num: x.coordinates.block_num,
                // line_num: x.coordinates.line_num,
                // word_num: x.coordinates.word_num
              }
            })
          });
        }
        catch{
          setState('error');
          return;
        }
      }
      setBackgroundImage('url('+dataURL+')');
      setCurrentTextBlocks(newCurrentTextBlockInformation);
      if(newCurrentTextBlockInformation.length < 1){
        setSelectedPage(null);
      }
      else{
        setSelectedPage(0);
      }
      setNaturalSize({width: newCurrentTextBlockInformation[0].imageWidth,height:newCurrentTextBlockInformation[0].imageHeight});
      setExtractedInformationsFromAllFiles([...extractedInformationsFromAllFiles,{filename: file.name, data:newCurrentTextBlockInformation}]);
      setState(null);
    }
  };
  const getTextBlocksElement = async() => {
    if(selectedPage === null){
      setTextBlockHTMLElement([<></>]);
      return;
    }
    let imageScalingSize: number = await calculateDisplaySize();
    let blockDivs: JSX.Element[] = []
    let idx = 0;
    for(let textblock of currentTextBlocks[selectedPage].textblocks){
      let marginTop = (textblock.top * imageScalingSize) -2;
      let marginLeft = (textblock.left * imageScalingSize) -2;
      let width = (textblock.width * imageScalingSize) + 4;
      let height = (textblock.height * imageScalingSize) + 4;
      let element = (<div 
        className='position-absolute border-textblocks' 
        style={{marginTop: String(marginTop)+'px', marginLeft: String(marginLeft)+'px', width:String(width)+'px', height:String(height)+'px', backgroundColor:'rgba(255,255,255,0.2)'}}
        onClick={e => setSelectedTextBlock(idx)}>
        </div>)
      blockDivs.push(element);
      idx += 1;
    };
    setTextBlockHTMLElement(blockDivs);
  };
  const calculateDisplaySize = () => {
    if (containerRef.current && naturalSize) {
      const containerWidth = containerRef.current.clientWidth;

      //Scaling
      const scale = containerWidth / naturalSize.width;

      setScaleParam(scale);
      return scale;
    }
    return 1;
  };

  
  const onClickButtonAttachFiles = (id:string) => {
    const fileUpload = document.getElementById(id);
    if(fileUpload){
      fileUpload.click();
    }
  };
  const deleteAttachedFile = (idx:number) => {
    //delete file in all files
    if(extractedInformationsFromAllFiles && extractedInformationsFromAllFiles.length > idx){
      let newData = [...extractedInformationsFromAllFiles];
      newData.splice(idx,1);
      setExtractedInformationsFromAllFiles(newData);
    }
  };
  const handleConfigurationChange = (targetName:string, targetValue:string,componentIndex:number) => {
    setConfigurationInputs((prevInputs) =>
      prevInputs.map((input, i) =>
        i === componentIndex ? { ...input, [targetName]: targetValue } : input
      )
    );
  };
  const handleAddConfigurationComponent = () => {
    setConfigurationInputs([...configurationInputs,emptyInput]);
  };
  const handleSaveConfiguration = () => {
    let jsonObject: ConfigurationJSONProps[] = configurationInputs.map( x=> {
      return{
        fieldName: x.fieldName,
        fieldType: x.dropdownSelectAI,
        request: x.requestForAI
      }
    });
    downloadJSON(jsonObject,configurationName);
  };
  const handleUploadConfiguration = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0]  : null;
    let errorText = '';
    if(file){
      //check if correct format
      const reader = new FileReader();
      let newConfigurationInputs: InputProps[] = [];
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target?.result as string);
          if (Array.isArray(json)) {
            json.forEach((item, index) => {
              if (checkUploadConfigurationProps(item)) {
                newConfigurationInputs.push({fieldName: item.fieldName,dropdownSelectAI: item.fieldType, requestForAI: item.request})
              } else {
                errorText = t('ocr.invalidObjectTypes');
              }
            });
            if(errorText=== ''){
              setConfigurationInputs(newConfigurationInputs);
            }
            else{
              setConfigurationUploadError(errorText);
            }
          } else {
            setConfigurationUploadError(t('ocr.notAnArray'))
          }
        } catch (error) {
          setConfigurationUploadError(t('ocr.uploadFailed', { error }));
        }
      };
      reader.readAsText(file);
    }
  };
  const checkUploadConfigurationProps = (item: any): item is ConfigurationJSONProps => {
    return (
      typeof item === "object" &&
      typeof item.fieldName === "string" &&
      typeof item.fieldType === "string"
    );
  };
  

  //USE EFFECTS
  useEffect(() => {
    document.title = t('ocr.documentTitle');
    let navbarTitle = document.getElementById('navbar_title');
    if(navbarTitle){
        navbarTitle.textContent = t('ocr.navbarTitle');
    }
    setConfigurationInputs([...configurationInputs,emptyInput]);
  },[]);
    // Anzeigengröße bei Fenster- oder Containergröße ändern
  useEffect(() => {
    window.addEventListener('resize', calculateDisplaySize);
    calculateDisplaySize();
    //cleanup event listener
    return () => window.removeEventListener('resize', calculateDisplaySize);
  }, [naturalSize]);
  useEffect(()=>{
    if(selectedPage !== null){
      setNaturalSize({width: currentTextBlocks[selectedPage].imageWidth,height: currentTextBlocks[selectedPage].imageHeight});
      setShowTextBlocks(false);
      setBackgroundImage(currentTextBlocks[selectedPage].image)
      getTextBlocksElement();
    }
  },[selectedPage,currentTextBlocks, scaleParam]);
  useEffect(() =>{
    const jsonObject = configurationInputs.reduce((acc,item)=>{
      acc[item.fieldName] = "";
      return acc;
    }, {} as Record<string,string>);
    setConfigurationsJSON(JSON.stringify(jsonObject));
  },[configurationInputs]);

  return (
    <div className="container-fluid">
      <div className='row  p-2' style={{height:'100%'}}>
        <div className='col-8'>
        <div className='row'>
          {state
            ? state === 'send'
                ? <p className='h4'>{t('state.sending')}</p>
                : state === 'waiting'
                    ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                    : state === 'error'
                        ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                        : state === 'done'
                            ? <p className='h4'>{stateIconDone} {t('state.done')}</p>
                            : <></>
            : <></>
        }
        </div>
        <div className='row p-2 border-bottom border-secondary'>
          <div className='col-auto p-2 pe-0'>
              <button 
                  onClick={() => navigateBack()} 
                  className="btn btn-outline-secondary btn-sm ms-3"
                  title={t('toolsApps:back')}
              >
              <FontAwesomeIcon
                      icon={faArrowLeftLong}
                      size="1x"
                      title={t('back')}
                  />
              </button>
          </div>
          <div className='col'>
            <div className='row'>
                <div className='col-md-auto'>
                  <div className="dropdown dropstart">
                    <button
                      className="btn btn-secondary"
                      type="button"
                      title={t('ocr.exampleDocuments')}
                      id="dropdownDocuments"
                      data-bs-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                      // disabled={attachedFileNames.length < 1}
                    >
                      {t('ocr.documents')}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-dark p-1 body-background border-secondary rounded" aria-labelledby="dropdownDocuments">
                      {extractedInformationsFromAllFiles.map(x=>x.filename).map((item,index) =>(
                        <li className="dropdown-item text-light ps-1"
                          onClick={async () => {
                            await setSelectedPage(0);
                            setCurrentTextBlocks(extractedInformationsFromAllFiles[index].data)
                          }}
                        >
                          <button
                            className='btn p-1 bg-transparent border-0 text-light me-1'
                            onClick={e => deleteAttachedFile(index)}
                            title={t('ocr.deleteDocument')}
                            data-bs-toggle='tooltip'
                          >
                            <FontAwesomeIcon
                              icon={faXmark}
                            />
                          </button>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
              </div>
              <div className='col-md-auto'>
                <button
                  className="btn btn-secondary"
                  title={t('ocr.addNewPdf')}
                  onClick={e => {
                    setShowTextBlocks(false);
                    onClickButtonAttachFiles('fileuploadDocuments')
                  }}
                >
                  <FontAwesomeIcon
                    className='me-1'
                    icon={faCirclePlus}
                  />
                  {t('ocr.addNewPdfsHere')}

                </button>
                <input
                    id = 'fileuploadDocuments'
                    type= 'file'
                    accept='application/pdf,image/png,image/jpg,image/jpeg'
                    onChange={event => uploadFile(event)}
                    hidden
                  />
              </div>
            </div>
          </div>
          <div className={`col text-center ${currentTextBlocks.length > 0 ? 'text-light' : 'text-secondary'}`}>
            <button
              className='btn border-0 bg-transparent'
              onClick={e => setSelectedPage(0)}
              disabled={currentTextBlocks.length < 1}
            >
              <FontAwesomeIcon
                icon={faAnglesLeft}
              />
            </button>
            <button
              className='btn border-0 bg-transparent'
              onClick={e => setSelectedPage(selectedPage !== null
                ? selectedPage-1 >= 0 
                  ? selectedPage-1 
                  : selectedPage 
                : null)}
              disabled={currentTextBlocks.length < 1}
            >
              <FontAwesomeIcon
                icon={faChevronLeft}
              />
            </button>
            {t('ocr.page')} {currentTextBlocks.length < 1 || selectedPage === null ? '' : selectedPage +1}
            <button
              className='btn border-0 bg-transparent'
              onClick={e => setSelectedPage(selectedPage !== null
                ? selectedPage+1 < currentTextBlocks.length 
                  ? selectedPage+1 
                  : selectedPage 
                : null)}
              disabled={currentTextBlocks.length < 1}
            >
              <FontAwesomeIcon
                icon={faChevronRight}
              />
            </button>
            <button
              className='btn border-0 bg-transparent'
              onClick={e => setSelectedPage(currentTextBlocks.length-1)}
              disabled={currentTextBlocks.length < 1}
            >
              <FontAwesomeIcon
                icon={faAnglesRight}
              />
            </button>
          </div>
          <div className='col'>
            <button
              className='btn btn-info float-end'
              title={showTextBlocks ? t('ocr.hideTextblocks') : t('ocr.showTextblocks')}
              onClick={e => setShowTextBlocks(!showTextBlocks)}
            >{showTextBlocks ? t('ocr.hideTextblocks') : t('ocr.showTextblocks')}
            </button>
          </div>
        </div>
        <div className='row overflow-auto bg-dark position-relative p-0' ref={containerRef} style={{maxHeight: '100vh'}}>
          { backgroundImage !== ''
            ? <img
                className='image-container p-0'
                src={backgroundImage}
                alt='background image'
              />
            : <></>

          }
          
          {
            showTextBlocks 
            ? <div className='w-100 position-absolute overlay-image-container p-0' ref={overlayRef} style={{backgroundColor: 'rgba(127,127,127,0.4)', minHeight: containerRef && containerRef.current?  containerRef.current.scrollHeight + 'px' : '100%'}}>
                { textBlockHTMElement.map((item,index)=>(
                  item
                ))}
              </div>
            : <></>
          }
        </div>
      </div>
      <div className="col d-flex flex-column p-2" style={{height: '100%'}}>
        <div className='row border-bottom border-secondary border-1'>
          <div className="col-12 d-flex flex-column p-0">
            <div className="flex-grow-1 overflow-auto" style={{height: sampleOutputAreaSize === defaultSampleOutputAreaSize ? "50vh" : "30vh", overflowY: "auto"}}>
              {configurationInputs.map((item,index)=>(
                <>
                  <ConfigurationComponent
                    index={index}
                    handleChange={handleConfigurationChange}
                    handleDrag={(fieldID: string) =>(console.log())}
                    componentID={'component_configuration_'+String(index)}
                    inputs={item}
                  />
                </>
              ))}
            </div>
          </div>
          <div className='row text-center mx-auto' style={{zIndex:'200'}}>
            <button
              className='btn btn-outline-secondary border-0'
              title={t('ocr.addConfiguration')}
              data-bs-toggle='tooltip'
              onClick={handleAddConfigurationComponent}
            >
              <FontAwesomeIcon
                icon={faPlus}
                className='p-0'
                size='xl'
              />
              {t('ocr.addNewField')}
            </button>
          </div>
        </div>
        <div className='row mb-2'>
          <div className='fw-bold fs-6'>
          {t('ocr.settingsTitle')}
          </div>
          <div className='mt-1 input-group'>
            <label className='input-group-text p-1'>{t('ocr.name')}</label>
            <input
              type='text'
              className='form-control rounded-end p-1'
              value={configurationName}
              onChange={e => setConfigurationName(e.target.value)}
            />
          </div>
        </div> 
        <div className='row mb-2 border-bottom border-secondary border-1 pb-2'>
            <div className='col'>
                <button
                className='btn btn-info my-1 p-1'
                >
                {t('ocr.testConfiguration')}
                </button>
            </div>
            <div className='col'>
                <button
                className='btn btn-info my-1 p-1 float-end'
                onClick={handleSaveConfiguration}
                >
                {t('ocr.saveConfiguration')}
                </button>
            </div>
        </div>
        <div className='d-flex mb-2'>
          <div className='col-md-auto fw-bold fs-6 p-0'>
            <button
              onClick={e =>setSampleOutputAreaSize(sampleOutputAreaSize === defaultSampleOutputAreaSize ? '50%' : defaultSampleOutputAreaSize)}
              className='rounded-top m-0 border-bottom-0'
            >
              <FontAwesomeIcon
                icon={sampleOutputAreaSize === defaultSampleOutputAreaSize ? faChevronRight : faChevronDown}
              />
            </button>
          </div>
          <div className='col-md-auto fw-bold fs-6 p-0'>
            <button
              onClick={e =>setShowOutputContent('sampleOutput')}
              className='rounded-top m-0 border-bottom-0'
              data-bs-toggle = 'tooltip'
              title={t('ocr.showOutputJson')}
            >
              {t('ocr.sampleOutput')}
            </button>
          </div>
          <div className='col-md-auto fw-bold fs-6 p-0'>
            <button
              onClick={e =>setShowOutputContent('fullText')}
              className='rounded-top m-0 border-bottom-0'
              data-bs-toggle = 'tooltip'
              title={t('ocr.showFullText')}
            >
              {t('ocr.fullText')}
            </button>
          </div>
          <div className='col fw-bold fs-6 p-0'>
            <button
              onClick={e =>setShowOutputContent('debug')}
              className='rounded-top m-0 border-bottom-0 float-end'
              data-bs-toggle = 'tooltip'
              title={t('ocr.debug')}
            >
              <FontAwesomeIcon
                icon={faBug}
              />
            </button>
          </div>
          
        </div>
        <div className='border border-secondary rounded flex-grow-1 overflow-auto'>
            {
              showOutoutContent === 'sampleOutput'
                ? configurationsJSON
                : showOutoutContent === 'fullText'
                  ? selectedPage !== null ? currentTextBlocks[selectedPage].text : ''
                  : <div className='p-2'>
                      <div className='row'>
                        <div className='col'>
                          <div className='h5'>{t('ocr.debugging')}</div>
                        </div>
                        <div className='col text-end'>
                          <button
                            className='bg-transparent border-0 align-middle'
                            data-bs-toggle= 'tooltip'
                            title={t('ocr.uploadConfiguration')}
                            onClick={e => onClickButtonAttachFiles('fileuploadConfiguration')}
                          >
                            <FontAwesomeIcon
                              icon={faUpload}
                            />
                          </button>
                          <input
                            id = 'fileuploadConfiguration'
                            type= 'file'
                            accept='application/json'
                            onChange={event => handleUploadConfiguration(event)}
                            hidden
                          />
                        </div>
                      </div>
                      <div className='fw-bold'>{t('ocr.readingMode')}</div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="readingModusRadio"
                          id="singleWordModu"
                          checked={ocrConfiguration=== '--psm 3'}
                          onClick={e => setOcrConfiguration('--psm 3')}
                        />
                        <label className="form-check-label" htmlFor="singleWordModu">
                          {t('ocr.singleWords')}
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="readingModusRadio"
                          id="blockModus"
                          checked={ocrConfiguration=== '--psm 6'}
                          onClick={e => {setOcrConfiguration('--psm 6')}}
                        />
                        <label className="form-check-label" htmlFor="blockModus">
                          {t('ocr.textblocks')}
                        </label>
                      </div>
                    </div>
            }
        </div>
      </div>
      {configurationInputs.map((item,index)=>(
        <ConfigurationModal
          modalID={'configurationModal_blockInput_'+String(index)}
          handleSaveConfiguration={(name:'name') => {}}
          items={{name: t('ocr.configName', { index })}}
        />
      ))}
      </div>
    </div>
  );
};